import { createSlice, createSelector } from '@reduxjs/toolkit';
import {
  selectProjectsIds as selectVisibleProjectsIds,
  selectIsProjectsListEmpty,
} from 'state/ProjectsList/projectsListSlice';
import { RootState } from 'state/store';

interface ExportState {
  selectAllProjects: boolean;
  selectedProjectsIds: string[];
  excludedProjectsIds: string[];
}

const initialState: ExportState = {
  selectAllProjects: false,
  selectedProjectsIds: [],
  excludedProjectsIds: [],
};

const exportSlice = createSlice({
  name: 'projectsList/export',
  initialState,
  reducers: {
    toggleSelectAllProjects: (state) => {
      state.selectAllProjects = !state.selectAllProjects;
      state.selectedProjectsIds = [];
      state.excludedProjectsIds = [];
    },
    toggleSelectedProject: (state, action) => {
      const projectId = action.payload;
      const areAllProjectsSelected = state.selectAllProjects;
      if (areAllProjectsSelected) {
        if (state.excludedProjectsIds.includes(projectId)) {
          state.excludedProjectsIds = state.excludedProjectsIds.filter(
            (id) => id !== projectId
          );
        } else {
          state.excludedProjectsIds.push(projectId);
        }
      } else {
        if (state.selectedProjectsIds.includes(projectId)) {
          state.selectedProjectsIds = state.selectedProjectsIds.filter(
            (id) => id !== projectId
          );
        } else {
          state.selectedProjectsIds.push(projectId);
        }
      }
    },
    resetSelectedProjects: (state) => {
      state.selectAllProjects = false;
      state.selectedProjectsIds = [];
      state.excludedProjectsIds = [];
    },
  },
});

export const {
  toggleSelectAllProjects,
  toggleSelectedProject,
  resetSelectedProjects,
} = exportSlice.actions;

const selectProjectsExportState = (state: RootState) =>
  state.projectsList.export;

export const selectAreAllProjectsSelected = (state: RootState) =>
  selectProjectsExportState(state).selectAllProjects;

export const selectSelectedProjectsIds = (state: RootState) =>
  Array.from(selectProjectsExportState(state).selectedProjectsIds);

export const selectExcludedProjectsIds = (state: RootState) =>
  Array.from(selectProjectsExportState(state).excludedProjectsIds);

export const selectAreProjectsPartiallySelected = createSelector(
  [
    selectAreAllProjectsSelected,
    selectSelectedProjectsIds,
    selectExcludedProjectsIds,
    selectVisibleProjectsIds,
  ],
  (
    areAllProjectsSelected,
    selectedProjectsIds,
    excludedProjectsIds,
    visibleProjectsIds
  ) => {
    return areAllProjectsSelected
      ? excludedProjectsIds.length > 0
      : selectedProjectsIds.length > 0 &&
          selectedProjectsIds.some((id) => visibleProjectsIds.includes(id));
  }
);

export const selectIsProjectSelected = createSelector(
  [
    selectProjectsExportState,
    (state: RootState, projectId: string) => projectId,
  ],
  (state, projectId) => {
    const selectedProjectsIds = state.selectedProjectsIds;
    const excludedProjectsIds = state.excludedProjectsIds;
    const areAllProjectsSelected = state.selectAllProjects;
    return areAllProjectsSelected
      ? !excludedProjectsIds.includes(projectId)
      : selectedProjectsIds.includes(projectId);
  }
);

export const selectCanExportProjects = createSelector(
  [
    selectAreProjectsPartiallySelected,
    selectAreAllProjectsSelected,
    selectIsProjectsListEmpty,
  ],
  (
    areProjectsPartiallySelected,
    areAllProjectsSelected,
    isProjectsListEmpty
  ) => {
    return (
      !isProjectsListEmpty &&
      (areProjectsPartiallySelected || areAllProjectsSelected)
    );
  }
);

export default exportSlice.reducer;
